import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { formatCapabilityLabel } from '../utils/capabilityContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function formatTimestamp(value) {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function statusLabel(value) {
  const labels = {
    pending_provider_response: 'Awaiting Response',
    open_for_proposals: 'Open for Proposals',
    proposal_submitted: 'Proposal Submitted',
    proposal_selected: 'Proposal Selected',
    in_fulfillment: 'In Fulfillment',
    not_selected: 'Not Selected',
  };
  return labels[value] || formatCapabilityLabel(value || 'unknown');
}

function statusColor(value) {
  if (['accepted', 'active', 'proposal_selected', 'in_fulfillment', 'selected', 'completed'].includes(value)) return 'success';
  if (['declined', 'cancelled', 'not_selected', 'withdrawn'].includes(value)) return 'error';
  return 'warning';
}

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function fieldName(field) {
  return field?.name || field?.key || '';
}

function fieldLabel(field) {
  return field?.label || field?.display_name || fieldName(field);
}

function renderValue(value) {
  if (value === null || typeof value === 'undefined' || value === '') return 'Not supplied';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

function CapabilitiesTable({ capabilities }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Fulfillment Capabilities</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Capability</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Need Category</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {capabilities.length ? capabilities.map((capability) => (
            <TableRow key={capability.request_capability_id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{capability.capability_name || 'Capability'}</Typography>
                <Typography variant="caption" color="text.secondary">{formatCapabilityLabel(capability.acceptance_mode || 'manual')}</Typography>
              </TableCell>
              <TableCell>{businessUnitLabel(capability.provider_business_unit)}</TableCell>
              <TableCell>{capability.need_category?.category_display_name || capability.need_category?.category_name || 'N/A'}</TableCell>
              <TableCell><Chip size="small" color={statusColor(capability.status)} label={statusLabel(capability.status)} /></TableCell>
              <TableCell align="right">
                <Button
                  component={RouterLink}
                  to={`/requests/direct/${capability.request_capability_id}`}
                  size="small"
                  variant="outlined"
                  startIcon={<RateReviewRoundedIcon />}
                >
                  Review
                </Button>
              </TableCell>
            </TableRow>
          )) : (
            <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No fulfillment Capabilities have been selected.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

function ArtifactsPanel({ artifacts, onDownload, downloadable }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Artifacts</Typography>
      {artifacts.length ? artifacts.map((artifact) => (
        <Stack key={`${artifact.field_key}:${artifact.file_id}`} direction="row" spacing={2} justifyContent="space-between" alignItems="center" sx={{ py: 1 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{artifact.filename || artifact.file_name || artifact.file_id}</Typography>
            <Typography variant="caption" color="text.secondary">{artifact.content_type || 'file'} · {artifact.field_key}</Typography>
          </Box>
          {downloadable ? <Button size="small" startIcon={<DownloadRoundedIcon />} onClick={() => onDownload(artifact)}>Download</Button> : null}
        </Stack>
      )) : <Typography variant="body2" color="text.secondary">No artifacts attached.</Typography>}
    </Paper>
  );
}

export default function RequestDetailPage(props) {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [expandedProposalId, setExpandedProposalId] = useState('');

  const loadRequest = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/requests/${encodeURIComponent(requestId)}`);
      setDetail(response.data || null);
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError?.message || 'Unable to load request.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequest();
  }, [requestId]);

  const request = detail?.request || {};
  const metadata = request.metadata || {};
  const capabilities = Array.isArray(detail?.capabilities) ? detail.capabilities : [];
  const artifacts = Array.isArray(detail?.general_artifacts) ? detail.general_artifacts : [];
  const proposals = Array.isArray(detail?.proposals) ? detail.proposals : [];
  const fields = Array.isArray(detail?.shared_fields) ? detail.shared_fields : [];
  const values = detail?.need_request_values || {};
  const desiredCapabilities = Array.isArray(detail?.desired_capabilities) ? detail.desired_capabilities : [];
  const workspaceId = detail?.workspace_id || request.workspace_id;
  const isCui = Boolean(request.is_cui ?? metadata.is_cui);
  const isNeedBased = request.request_type === 'need_based' || detail?.request_type === 'need_based';

  const selectProposal = async (proposal) => {
    if (!window.confirm(`Select the solution proposed by ${businessUnitLabel(proposal.provider_business_unit)}?`)) return;
    setBusy(true);
    setError('');
    setFeedback('');
    try {
      const response = await axiosInstance.post(
        `/requests/${encodeURIComponent(requestId)}/proposals/${encodeURIComponent(proposal.proposal_id)}/select`,
      );
      setDetail(response.data || null);
      setFeedback('Proposal selected. The Capability workstreams and fulfillment workspace are ready.');
    } catch (selectError) {
      setError(selectError?.response?.data?.message || selectError?.message || 'Unable to select proposal.');
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    if (!window.confirm('Cancel this need-based request? Active fulfillment workstreams will be cancelled.')) return;
    setBusy(true);
    setError('');
    try {
      const response = await axiosInstance.post(`/requests/${encodeURIComponent(requestId)}/cancel`);
      setDetail(response.data || null);
      setFeedback('Request cancelled.');
    } catch (cancelError) {
      setError(cancelError?.response?.data?.message || cancelError?.message || 'Unable to cancel request.');
    } finally {
      setBusy(false);
    }
  };

  const downloadArtifact = async (artifact) => {
    try {
      const response = await axiosInstance.post(
        `/requests/need-opportunities/${encodeURIComponent(requestId)}/artifacts/${encodeURIComponent(artifact.file_id)}/download`,
      );
      const url = response.data?.download_url || response.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (downloadError) {
      setError(downloadError?.response?.data?.message || downloadError?.message || 'Unable to download artifact.');
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header breadcrumbItems={[
            { label: 'AMX xConsole', href: '/' },
            { label: 'Requests', href: '/requests' },
            { label: request.title || 'Request' },
          ]} />

          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center"><CircularProgress size={24} /><Typography>Loading request...</Typography></Stack>
          ) : error && !detail ? (
            <Alert severity="error">{error}</Alert>
          ) : detail ? (
            <Stack spacing={3} sx={{ maxWidth: 1120 }}>
              <ClassificationBanner isCui={isCui} />
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                  {isNeedBased ? <Typography variant="overline" color="primary">Need-Based Request</Typography> : null}
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>{request.title || 'Request'}</Typography>
                  <Typography variant="body2" color="text.secondary">{businessUnitLabel(detail.requesting_business_unit)}</Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip color={statusColor(detail.status)} label={statusLabel(detail.status)} />
                  <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/requests')}>Requests</Button>
                  {isNeedBased && detail.authorization?.can_cancel ? (
                    <Button color="error" startIcon={<CancelRoundedIcon />} disabled={busy} onClick={cancelRequest}>Cancel</Button>
                  ) : null}
                  {workspaceId ? (
                    <Button component={RouterLink} to={`/workspaces/${workspaceId}`} variant="contained" startIcon={<OpenInNewRoundedIcon />}>Workspace</Button>
                  ) : null}
                </Stack>
              </Stack>
              {error ? <Alert severity="error">{error}</Alert> : null}
              {feedback ? <Alert severity="success">{feedback}</Alert> : null}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Request Details</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                    <Box sx={{ flex: 1 }}><Typography variant="caption" color="text.secondary">Submitted</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTimestamp(request.created_at)}</Typography></Box>
                    <Box sx={{ flex: 1 }}><Typography variant="caption" color="text.secondary">Needed By</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{metadata.needed_by || 'N/A'}</Typography></Box>
                    <Box sx={{ flex: 1 }}><Typography variant="caption" color="text.secondary">Priority</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{metadata.priority ? formatCapabilityLabel(metadata.priority) : 'N/A'}</Typography></Box>
                  </Stack>
                  {isNeedBased ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Need Category</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 800 }}>{detail.need_category?.category_display_name || detail.need_category?.category_name}</Typography>
                    </Box>
                  ) : null}
                  {request.description ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{request.description}</Typography> : null}
                  {metadata.general_notes ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{metadata.general_notes}</Typography> : null}
                </Stack>
              </Paper>

              {isNeedBased ? (
                <>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Need Requirements</Typography>
                    <Table size="small">
                      <TableBody>
                        {fields.map((field) => (
                          <TableRow key={fieldName(field)}>
                            <TableCell sx={{ width: 300, fontWeight: 700 }}>{fieldLabel(field)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderValue(values[fieldName(field)])}</TableCell>
                          </TableRow>
                        ))}
                        {!fields.length ? <TableRow><TableCell><Typography color="text.secondary">No structured requirements.</Typography></TableCell></TableRow> : null}
                      </TableBody>
                    </Table>
                    {desiredCapabilities.length ? (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Desired Capabilities (Requester Reference)</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                          {desiredCapabilities.map((capability) => <Chip key={capability.capability_definition_id} variant="outlined" label={capability.capability_name} />)}
                        </Stack>
                      </Box>
                    ) : null}
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Proposed Solutions</Typography>
                        <Typography variant="body2" color="text.secondary">Compare complete fulfillment approaches rather than selecting individual services.</Typography>
                      </Box>
                      <Chip label={`${proposals.length} Proposal${proposals.length === 1 ? '' : 's'}`} />
                    </Stack>
                    <Stack spacing={2}>
                      {proposals.length ? proposals.map((proposal) => (
                        <Paper key={proposal.proposal_id} variant="outlined" sx={{ p: 2, borderColor: proposal.status === 'selected' ? 'success.main' : undefined }}>
                          <Stack spacing={1.5}>
                            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                              <Box>
                                <Typography variant="overline" color="text.secondary">Proposed Solution</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>{businessUnitLabel(proposal.provider_business_unit)}</Typography>
                              </Box>
                              <Chip color={statusColor(proposal.status)} label={statusLabel(proposal.status)} />
                            </Stack>
                            <Typography color="primary" sx={{ fontSize: '1.05rem', fontWeight: 800 }}>
                              {proposal.capabilities.map((capability) => capability.capability_name).join(' → ')}
                            </Typography>
                            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{proposal.explanation}</Typography>
                            {expandedProposalId === proposal.proposal_id && proposal.sequencing_notes ? <Alert severity="info">{proposal.sequencing_notes}</Alert> : null}
                            {expandedProposalId === proposal.proposal_id && proposal.capabilities.some((capability) => capability.dependency_info?.notes) ? (
                              <Stack spacing={0.5}>
                                {proposal.capabilities.filter((capability) => capability.dependency_info?.notes).map((capability) => (
                                  <Typography key={capability.proposal_item_id} variant="body2">
                                    <strong>{capability.capability_name}:</strong> {capability.dependency_info.notes}
                                  </Typography>
                                ))}
                              </Stack>
                            ) : null}
                            {expandedProposalId === proposal.proposal_id ? (
                              <Typography variant="caption" color="text.secondary">Submitted {formatTimestamp(proposal.submitted_at)}</Typography>
                            ) : null}
                            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                              <Button
                                variant="outlined"
                                onClick={() => setExpandedProposalId((current) => current === proposal.proposal_id ? '' : proposal.proposal_id)}
                              >
                                {expandedProposalId === proposal.proposal_id ? 'Hide Details' : 'Review Proposal'}
                              </Button>
                              {proposal.status === 'submitted' && detail.authorization?.can_select_proposal ? (
                                <Button variant="contained" disabled={busy} onClick={() => selectProposal(proposal)}>Select Proposal</Button>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Paper>
                      )) : <Alert severity="info">Qualified provider Business Units can review this need and submit Capability solution plans.</Alert>}
                    </Stack>
                  </Paper>
                </>
              ) : null}

              <CapabilitiesTable capabilities={capabilities} />
              <ArtifactsPanel artifacts={artifacts} onDownload={downloadArtifact} downloadable={isNeedBased} />
            </Stack>
          ) : <Alert severity="error">Request not found.</Alert>}
        </Box>
      </Box>
    </AppTheme>
  );
}
